import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { Button, Tooltip } from '@gitbook/spine-react';
import { useLatestRef, useLoadingState } from '@gitbook/util-react-hooks';

import { setKeyboardShortcutsScope, shortcuts, useKeyboardShortcuts } from '../../../Keyboard';

/**
 * Button to save and close the drawing, with keyboard bindings.
 * When pressing ESCAPE, we indicate to the user that they need to press it twice to close it.
 */
export function CloseDrawingButton(props: { onClose: () => Promise<void> }): React.ReactElement {
    const { onClose } = props;

    const [loading, wrapLoading] = useLoadingState();

    const [pressedEscapeOnce, setPressedEscapeOnce] = React.useState(false);
    const pressedEscapeOnceRef = useLatestRef(pressedEscapeOnce);
    const onCloseRef = useLatestRef(onClose);
    const loadingRef = useLatestRef(loading);

    const onCloseWithLoading = React.useCallback(() => {
        if (loadingRef.current) {
            return;
        }

        wrapLoading(() => {
            onCloseRef.current();
        });
    }, [onCloseRef, loadingRef]);

    // Bind the keyboard shortcut to close
    React.useEffect(() => {
        return setKeyboardShortcutsScope('editor-block-modal');
    }, []);
    useKeyboardShortcuts(
        {
            [shortcuts.EDITOR_SAVE]: () => {
                onCloseWithLoading();
            },
        },
        'editor-block-modal'
    );

    React.useEffect(() => {
        let timeout: NodeJS.Timeout | null = null;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return;
            }

            if (pressedEscapeOnceRef.current) {
                if (timeout) {
                    clearTimeout(timeout);
                }

                onCloseWithLoading();
            } else {
                setPressedEscapeOnce(true);

                timeout = setTimeout(() => {
                    setPressedEscapeOnce(false);
                }, 2000);
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [pressedEscapeOnceRef, onCloseWithLoading]);

    const tooltipProps = pressedEscapeOnce
        ? ({
              label: 'Save & Close',
              description: 'Press again to save & close',
              shortcut: 'ESC',
          } as const)
        : ({
              label: 'Save & Close',
              shortcut: shortcuts.EDITOR_SAVE,
          } as const);
    return (
        <Tooltip {...tooltipProps} position="left">
            <Button
                label="Close"
                leadingIcon={Icon.X}
                kind="blank"
                iconOnly
                disabled={loading}
                onPress={onCloseWithLoading}
            />
        </Tooltip>
    );
}
